/** @format */

import {CogIcon, TrashIcon} from '@heroicons/react/outline'
import clsx from 'clsx'
import React, {useEffect, useMemo, useState} from 'react'

import {Loading} from '@src/components/Loading'
import {ActionMenu, ActionMenuItem} from '@src/components/tailwind/ActionMenu'
import {getAppLogoByPluginKind} from '@src/components/tailwind/AppLogo'
import {
  OpenModalButton,
  useModalContext,
} from '@src/components/tailwind/ModalContext'

import * as toast from '@src/toast'
import {PluginKind} from '@src/types/Plugin'

import {ADD_FIELD_MODAL_ID, AddFieldModal} from './AddFieldModal'
import {DeleteFieldModal} from './DeleteFieldModal'
import {EditFieldModal} from './EditFieldModal'
import {getDisplayNameForFieldType, getIconByFieldType} from './FieldType'
import {FieldValidation} from './FieldValidation'
import {useValidationApi} from './ValidationApi'

export function ValidationGroup() {
  const api = useValidationApi()

  useEffect(() => {
    if (api.listValidations.error) {
      toast.error(api.listValidations.error)
    }
  }, [api.listValidations.error])

  const [lastAddedField, setLastAddedField] = useLastAddedField({
    clearDelayMs: 3 * 1000,
  })

  return (
    <article className="mx-auto max-w-3xl overflow-hidden rounded-lg border-2 border-dashed border-gray-300 bg-white/70">
      <header className="flex items-center gap-4 px-8 py-2">
        <h3 className="spacing-wide grow text-sm uppercase text-gray-400">
          Validation
        </h3>
        <OpenModalButton
          modal={ADD_FIELD_MODAL_ID}
          className="shrink-0 rounded-lg p-2 text-sm font-bold leading-none text-primary hover:bg-gray-50"
        >
          + Add New
        </OpenModalButton>
      </header>

      {api.listValidations.isLoading ? (
        <div className="grid min-h-[12rem] place-items-center">
          <Loading />
        </div>
      ) : (
        <ValidationList
          highlightedField={lastAddedField}
          validations={api.listValidations.data}
        />
      )}

      <AddFieldModal onSuccess={setLastAddedField} />
    </article>
  )
}

type ValidationListProps = {
  highlightedField: string | null
  validations: FieldValidation[]
}

function ValidationList(props: ValidationListProps) {
  const validations = useMemo(
    () =>
      [...props.validations].sort((v1, v2) => {
        const fn1 = v1.fieldName.toLowerCase()
        const fn2 = v2.fieldName.toLowerCase()
        if (fn1 < fn2) {
          return -1
        }
        if (fn1 > fn2) {
          return 1
        }
        return 0
      }),
    [props.validations],
  )
  return (
    <section className="grid gap-4 px-8 pb-4 xl:grid-cols-2">
      {validations.map(v => (
        <ValidationListItem
          key={v.fieldName}
          highlighted={v.fieldName === props.highlightedField}
          validation={v}
        />
      ))}
    </section>
  )
}

type ValidationListItemProps = {
  highlighted: boolean
  validation: FieldValidation
}

function ValidationListItem(props: ValidationListItemProps) {
  const {highlighted, validation} = props
  const FieldTypeIcon = getIconByFieldType(validation.type.value)

  // gets an aggregate set of action names (PluginKind) in FieldValidation
  // from all validation rules.
  const actionsSummary = useMemo(() => {
    const plugins = new Set<PluginKind>()
    for (const v of Object.values(validation)) {
      if (v.plugins instanceof Set) {
        ;(v.plugins as Set<PluginKind>).forEach(p => plugins.add(p))
      }
    }
    return plugins
  }, [validation])

  const {openModal} = useModalContext()
  const editModalId = `valiation.edit-field.${validation.fieldName}`
  const deleteModalId = `valiation.delete-field.${validation.fieldName}`
  const canDelete = validation.plugins == null

  return (
    <>
      <div
        className={clsx(
          'flex overflow-hidden rounded border bg-white transition duration-300',
          highlighted
            ? 'border-primary ring-4 ring-primary/30'
            : 'border-gray-200',
        )}
      >
        <div className="shrink-0 bg-primary p-4">
          {/* add padding to vertically center the icon to the first two lines of the card body */}
          <div style={{paddingTop: 5}}>
            <FieldTypeIcon className="h-8 w-8 text-white" />
          </div>
        </div>
        <div className="flex grow flex-col gap-1 p-4">
          <div className="flex items-center justify-between">
            <code className="text-sm font-semibold leading-none text-gray-800">
              {validation.fieldName}
              {validation.required?.value ? (
                <sup
                  aria-hidden="true"
                  className="ml-1 select-none text-primary"
                >
                  *
                </sup>
              ) : null}
            </code>
            <div className="shrink-0 translate-x-1.5">
              <ActionMenu alignContent="start" focusTriggerOnClose={false}>
                <ActionMenuItem
                  icon={<CogIcon />}
                  label="Settings"
                  onClick={() => openModal(editModalId)}
                />
                <ActionMenuItem
                  disabled={!canDelete}
                  color="danger"
                  icon={<TrashIcon />}
                  label="Delete"
                  onClick={() => openModal(deleteModalId)}
                />
              </ActionMenu>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            <span>{getDisplayNameForFieldType(validation.type.value)}</span>
            <span>
              , {validation.required?.value ? 'required' : 'not required'}
            </span>
          </p>
          {actionsSummary.size > 0 && (
            <div className="flex items-center gap-2">
              <p className="text-xs text-gray-500">Used by</p>
              {[...actionsSummary].map(pluginKind => {
                const AppLogo = getAppLogoByPluginKind(pluginKind)
                return AppLogo == null ? null : (
                  <AppLogo
                    key={pluginKind}
                    fontSize="0.75rem"
                    className="text-gray-500"
                  />
                )
              })}
            </div>
          )}
        </div>
      </div>

      <EditFieldModal modal={editModalId} validation={validation} />

      {canDelete && (
        <DeleteFieldModal modal={deleteModalId} validation={validation} />
      )}
    </>
  )
}

// Manages state for identifying the most recently added validation field.
// The value is cleared (set to null) after the specified delay.
function useLastAddedField({
  clearDelayMs,
}: {
  // The delay, in milliseconds, before the field name is cleared.
  clearDelayMs: number
}): [string | null, (fieldName: string) => void] {
  const [lastAddedField, setLastAddedField] = useState<string | null>(null)

  // Clear the highlighted field after a timeout.
  useEffect(() => {
    if (lastAddedField == null) {
      return
    }
    const timer = setTimeout(() => setLastAddedField(null), clearDelayMs)
    return () => {
      clearTimeout(timer)
    }
  }, [lastAddedField, clearDelayMs])

  return [lastAddedField, setLastAddedField]
}
